import React, { useEffect, useRef, useState } from "react";
import { ImagePlus, ArrowLeft, ArrowRight, RefreshCw, Trash2, FileText, X, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { fileBlobUrl, isImage } from "../../lib/research";

const ACCEPT = "image/png,image/jpeg,image/webp,image/gif,application/pdf";
const MAX = 10 * 1024 * 1024;

export const BlobImg = ({ fid, className, alt = "" }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => { let on = true; fileBlobUrl(fid).then((u) => on && setSrc(u)).catch(() => {}); return () => { on = false; }; }, [fid]);
  return src ? <img src={src} alt={alt} className={className} /> : <div className={`${className} bg-slate-100 animate-pulse`} />;
};

const Thumb = ({ f, first, last, onMove, onChange, onZoom }) => {
  const [cap, setCap] = useState(f.caption || "");
  const ref = useRef(null);
  const saveCap = () => { if (cap !== (f.caption || "")) api.patch(`/research/files/${f.id}`, { caption: cap }).then(onChange).catch(() => toast.error("कैप्शन सेव नहीं हुआ")); };
  const replace = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.put(`/research/files/${f.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); onChange(); } catch { toast.error("बदलना असफल"); }
  };
  const del = async () => { if (!window.confirm("यह फाइल हटाएँ?")) return; await api.delete(`/research/files/${f.id}`).catch(() => {}); onChange(); };
  const open = async () => { if (isImage(f)) onZoom(f); else window.open(await fileBlobUrl(f.id), "_blank"); };
  return (
    <div className="w-40 shrink-0 rounded-md border border-slate-200 bg-white p-1.5" data-testid={`rfile-${f.id}`}>
      <button type="button" onClick={open} className="block w-full" data-testid={`rfile-open-${f.id}`}>
        {isImage(f) ? <BlobImg fid={f.id} className="w-full h-24 object-contain bg-slate-50 rounded" /> :
          <div className="w-full h-24 flex flex-col items-center justify-center bg-red-50 rounded text-red-700 text-xs"><FileText className="w-6 h-6" />{f.original_filename}</div>}
      </button>
      <input value={cap} onChange={(e) => setCap(e.target.value)} onBlur={saveCap} placeholder="कैप्शन…" className="mt-1 w-full text-xs border-b border-slate-200 focus:outline-none focus:border-teal-600 py-0.5" data-testid={`rfile-caption-${f.id}`} />
      <div className="flex justify-between mt-1 text-slate-500">
        <button type="button" disabled={first} onClick={() => onMove(-1)} className="p-1 disabled:opacity-30" title="पहले"><ArrowLeft className="w-3 h-3" /></button>
        <button type="button" disabled={last} onClick={() => onMove(1)} className="p-1 disabled:opacity-30" title="बाद में"><ArrowRight className="w-3 h-3" /></button>
        <button type="button" onClick={() => ref.current?.click()} className="p-1" title="बदलें" data-testid={`rfile-replace-${f.id}`}><RefreshCw className="w-3 h-3" /></button>
        <button type="button" onClick={del} className="p-1 text-red-600" title="हटाएँ" data-testid={`rfile-delete-${f.id}`}><Trash2 className="w-3 h-3" /></button>
        <input ref={ref} type="file" accept={ACCEPT} className="hidden" onChange={replace} />
      </div>
    </div>
  );
};

export const Lightbox = ({ f, onClose }) => (
  <div className="fixed inset-0 z-[120] bg-black/85 flex flex-col items-center justify-center p-4" onClick={onClose} data-testid="rfile-lightbox">
    <button className="absolute top-4 right-4 text-white" onClick={onClose}><X className="w-6 h-6" /></button>
    <BlobImg fid={f.id} className="max-w-full max-h-[85vh] object-contain" />
    {f.caption && <div className="text-white text-sm mt-3">{f.caption}</div>}
  </div>
);

// files: already filtered list; uploadUrl + extra form fields; onChange reloads parent list
export const FileStrip = ({ files, uploadUrl, extra = {}, onChange, testid, pasteTarget }) => {
  const [queue, setQueue] = useState([]);
  const [zoom, setZoom] = useState(null);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);
  const sorted = [...files].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const upload = async (file, key = Math.random().toString(36).slice(2)) => {
    if (file.size > MAX) { toast.error(`${file.name}: 10MB से बड़ी फाइल`); return; }
    if (!ACCEPT.split(",").includes(file.type)) { toast.error(`${file.name}: केवल PNG/JPG/WEBP/GIF/PDF`); return; }
    setQueue((q) => [...q.filter((x) => x.key !== key), { key, file, progress: 0, error: false }]);
    const fd = new FormData(); Object.entries(extra).forEach(([k, v]) => v && fd.append(k, v)); fd.append("file", file);
    try {
      await api.post(uploadUrl, fd, { headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setQueue((q) => q.map((x) => (x.key === key ? { ...x, progress: Math.round((e.loaded / (e.total || 1)) * 100) } : x))) });
      setQueue((q) => q.filter((x) => x.key !== key)); onChange();
    } catch { setQueue((q) => q.map((x) => (x.key === key ? { ...x, error: true } : x))); }
  };
  const addFiles = (list) => Array.from(list || []).forEach((f) => upload(f));

  useEffect(() => {
    const el = pasteTarget?.current; if (!el) return;
    const onPaste = (e) => { const fs = Array.from(e.clipboardData?.files || []); if (fs.length) { e.preventDefault(); addFiles(fs); } };
    el.addEventListener("paste", onPaste);
    return () => el.removeEventListener("paste", onPaste);
  });

  const move = async (i, dir) => {
    const a = sorted[i], b = sorted[i + dir];
    await Promise.all([api.patch(`/research/files/${a.id}`, { order: i + dir }), api.patch(`/research/files/${b.id}`, { order: i })]).catch(() => {});
    onChange();
  };

  return (
    <div data-testid={testid}>
      <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
        className={`rounded-md border-2 border-dashed p-2 transition-colors ${drag ? "border-teal-500 bg-teal-50" : "border-slate-200"}`}>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {sorted.map((f, i) => <Thumb key={f.id} f={f} first={i === 0} last={i === sorted.length - 1} onMove={(d) => move(i, d)} onChange={onChange} onZoom={setZoom} />)}
          {queue.map((x) => (
            <div key={x.key} className="w-40 shrink-0 rounded-md border border-slate-200 p-2 text-xs flex flex-col justify-center" data-testid="rfile-uploading">
              <div className="truncate">{x.file.name}</div>
              {x.error ? <button type="button" onClick={() => upload(x.file, x.key)} className="mt-1 inline-flex items-center gap-1 text-red-600" data-testid="rfile-retry"><RotateCw className="w-3 h-3" /> असफल — Retry</button> :
                <div className="mt-1 h-1.5 bg-slate-100 rounded"><div className="h-1.5 bg-teal-600 rounded" style={{ width: `${x.progress}%` }} /></div>}
            </div>
          ))}
          <button type="button" onClick={() => inputRef.current?.click()} className="w-40 h-[150px] shrink-0 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 flex flex-col items-center justify-center text-xs gap-1" data-testid={`${testid}-add`}>
            <ImagePlus className="w-5 h-5" /> स्क्रीनशॉट / PDF जोड़ें
            <span className="text-[10px] text-slate-400 px-2 text-center">Drag-drop या Ctrl+V · PNG/JPG/WEBP/GIF/PDF · max 10MB</span>
          </button>
          <input ref={inputRef} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid={`${testid}-input`} />
        </div>
      </div>
      {zoom && <Lightbox f={zoom} onClose={() => setZoom(null)} />}
    </div>
  );
};
